import { toast } from 'react-toastify';
import { MAX_INTERNAL_WORKSPACES, SpaceErrorCode, WorkspaceListItem } from 'shared';

import { BackendRoute } from '../../../config/index';
import { QueryKey } from '../../../constants/index';
import { HttpException } from '../../../exceptions/index';
import { useAppMutation, useInvalidateQueries, useLocale } from '../../../hooks/index';
import { ApiService } from '../../../services/index';
import { selectedCompanyStorage } from '../../../storage/company/selected-company.util';
import { WORKSPACE_INVALIDATION_KEYS } from '../constants/spaces-query.const';
import { CreateWorkspacePayload } from '../validations/create-workspace.validation';

type Props = {
  onDuplicateName?: () => void;
  onSuccess?: (workspace: WorkspaceListItem) => void;
};

export const useCreateWorkspace = ({ onDuplicateName, onSuccess }: Props = {}) => {
  const companyId = selectedCompanyStorage(({ companyId: selected }) => selected);
  const { messagesLocale, messagesLocaleFn } = useLocale();
  const { invalidateQuery } = useInvalidateQueries(...WORKSPACE_INVALIDATION_KEYS);

  const { isPending, mutate: createWorkspace } = useAppMutation<
    WorkspaceListItem,
    HttpException,
    CreateWorkspacePayload
  >([QueryKey.CREATE_WORKSPACE, companyId], {
    // Replaced: a taken name goes to the field, anything else gets a translated toast by its code.
    defaultErrorHandling: false,
    mutationFn: (body) =>
      ApiService.post<WorkspaceListItem, CreateWorkspacePayload>(
        { endpoint: BackendRoute.WORKSPACES },
        { body },
      ),
    onError: (error) => {
      const code = error.body?.code;

      if (code === SpaceErrorCode.WORKSPACE_NAME_TAKEN) {
        onDuplicateName?.();

        return;
      }

      toast.error(
        code === SpaceErrorCode.WORKSPACE_LIMIT_REACHED
          ? messagesLocaleFn('WORKSPACE_LIMIT_REACHED', { max: MAX_INTERNAL_WORKSPACES })
          : messagesLocale['FAILED_TO_SAVE_CHANGES'],
      );
    },
    onSuccess: (workspace) => {
      toast.success(messagesLocale['WORKSPACE_CREATED']);
      invalidateQuery();
      onSuccess?.(workspace);
    },
  });

  return { createWorkspace, isPending };
};
